// layers/base/app/i18n/normalizeLocale.ts
//
// Maps a raw locale string (navigator.language, an Accept-Language entry, or a
// stored value) onto one of our SUPPORTED_CODES. Order of attempts:
//   1. exact match (case-insensitive, region re-cased)
//   2. explicit alias (zh-* -> zh, pt -> pt-BR)
//   3. base-language match (en-GB -> en)
//   4. DEFAULT_LOCALE
import { DEFAULT_LOCALE, SUPPORTED_CODES } from './locales'

// Aliases for languages whose supported code is not the bare base tag, or whose
// many regional variants collapse to one supported code.
const ALIASES: Record<string, string> = {
  zh: 'zh',
  'zh-hans': 'zh',
  'zh-hant': 'zh',
  'zh-cn': 'zh',
  'zh-tw': 'zh',
  'zh-hk': 'zh',
  pt: 'pt-BR',
  'pt-pt': 'pt-BR',
}

function recase(code: string): string {
  // Normalize "pt-br" -> "pt-BR" so an exact compare against SUPPORTED_CODES works.
  const [lang, region] = code.split('-')
  return region ? `${lang.toLowerCase()}-${region.toUpperCase()}` : lang.toLowerCase()
}

export function normalizeLocale(raw: string | undefined | null): string {
  if (!raw) return DEFAULT_LOCALE
  const lower = raw.trim().toLowerCase()
  if (!lower) return DEFAULT_LOCALE

  // 1. exact match (re-cased)
  const recased = recase(lower)
  if (SUPPORTED_CODES.includes(recased)) return recased

  // 2. alias (full tag, then base)
  const base = lower.split('-')[0]
  if (ALIASES[lower]) return ALIASES[lower]
  if (ALIASES[base]) return ALIASES[base]

  // 3. base-language match (en-GB -> en)
  const baseMatch = SUPPORTED_CODES.find((c) => c.toLowerCase().split('-')[0] === base)
  if (baseMatch) return baseMatch

  // 4. fallback
  return DEFAULT_LOCALE
}
